import React, { useState, useEffect } from 'react';
import { Building2, Upload, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

console.log('✅ OrganizationSettings.jsx loaded!');

const API_URL = process.env.REACT_APP_API_URL;
const API_BASE_URL = (process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://app.alpenscreen.ch');

const OrganizationSettings = ({ currentUser, onUpdate }) => {
  const { t } = useTranslation();
  
  console.log('🚀 OrganizationSettings RENDERING NOW!');
  console.log('   Received currentUser:', currentUser);
  
  const [name, setName] = useState('');
  const [color, setColor] = useState('#2563eb');
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoDeleted, setLogoDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Load current organization data
  useEffect(() => {
    console.log('========================================');
    console.log('🔄 useEffect TRIGGERED!');
    console.log('========================================');
    
    try {
      // Priorität 1: currentUser.organization (für Super Admin viewing andere Org)
      if (currentUser?.organization) {
        console.log('📋 Loading from currentUser prop:', currentUser.organization);
        loadOrganizationDataFromObject(currentUser.organization);
      }
      // Fallback: localStorage
      else {
        const userStr = localStorage.getItem('user');
        console.log('🔍 Raw localStorage user:', userStr);
        
        if (userStr) {
          const user = JSON.parse(userStr);
          console.log('👤 Parsed user:', user);
          console.log('🏢 User organization:', user.organization);
          
          if (user.organization) {
            loadOrganizationDataFromObject(user.organization);
          }
        }
      }
      
      setInitialized(true);
    } catch (error) {
      console.error('❌ Error in useEffect:', error);
    }
  }, [currentUser]); // ✅ Re-run wenn currentUser sich ändert!

  // Separate function to load data from organization object
  const loadOrganizationDataFromObject = (org) => {
    try {
      console.log('📦 Loading organization data from object:');
      console.log('   ID:', org.id);
      console.log('   Name:', org.name);
      console.log('   Color:', org.color);
      console.log('   Logo URL:', org.logo_url);
      
      setName(org.name || '');
      setColor(org.color || '#2563eb');
      
      if (org.logo_url) {
        const fullLogoUrl = `${API_BASE_URL}${org.logo_url}`;
        console.log('🖼️ Setting logo preview:', fullLogoUrl);
        setLogoPreview(fullLogoUrl);
        setLogoDeleted(false);
      } else {
        console.log('❌ No logo_url - clearing preview');
        setLogoPreview(null);
        setLogoDeleted(false);
      }
      
      setLogo(null);
      console.log('✅ loadOrganizationDataFromObject completed');
    } catch (error) {
      console.error('❌ Error loading org data:', error);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: t('organization.errors.fileSizeTooLarge') });
        return;
      }

      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: t('organization.errors.fileMustBeImage') });
        return;
      }

      setLogo(file);
      setLogoDeleted(false);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      e.target.value = null;
    }
  };

  const handleRemoveLogo = () => {
    console.log('🗑️ Remove logo clicked');
    setLogo(null);
    setLogoPreview(null);
    setLogoDeleted(true);
    console.log('✅ Logo removed from state');
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage(null);

    console.log('💾 Save started');
    console.log('   logoDeleted:', logoDeleted);
    console.log('   logo (new file):', logo);
    console.log('   logoPreview:', logoPreview);
    console.log('   currentUser:', currentUser);

    try {
      const token = localStorage.getItem('token');
      const viewingOrgId = localStorage.getItem('viewingAsOrgId');
      const userRole = localStorage.getItem('userRole');
      
      // ✅ Determine which organization to update
      let targetOrgId = currentUser?.organization?.id;
      
      if (userRole === 'super_admin' && viewingOrgId) {
        targetOrgId = parseInt(viewingOrgId);
        console.log('🔧 Super Admin updating org:', targetOrgId);
      }
      
      console.log('🎯 Target Organization ID:', targetOrgId);
      
      let logoUrl = null;

      // Handle logo
      if (logoDeleted) {
        console.log('🗑️ Deleting logo via API...');
        const deleteUrl = `${API_URL}/organizations/current/logo`;
        try {
          const deleteResponse = await fetch(deleteUrl, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } catch (error) {
          console.error('Error deleting logo:', error);
        }
        logoUrl = null;
      }
      else if (logo) {
        console.log('📤 Uploading new logo...');
        const formData = new FormData();
        formData.append('file', logo);

        // ✅ Upload with filter_org for Super Admin
        const uploadUrl = `${API_URL}/organizations/current/logo`;

        const uploadResponse = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!uploadResponse.ok) {
          throw new Error(t('organization.errors.logoUploadFailed'));
        }

        const uploadData = await uploadResponse.json();
        logoUrl = uploadData.logo_url;
        console.log('✅ Logo uploaded:', logoUrl);
      }
      else if (currentUser?.organization?.logo_url) {
        console.log('📌 Keeping existing logo');
        logoUrl = currentUser.organization.logo_url;
      }

      console.log('📡 Sending to backend - logoUrl:', logoUrl);

      // ✅ Build URL based on role
      let url = `${API_URL}/organizations/current`;
      
      if (userRole === 'super_admin' && targetOrgId) {
        url = `${API_URL}/organizations/${targetOrgId}`;
        console.log('🔧 Super Admin URL:', url);
      }

      // Update organization
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name,
          color,
          logo_url: logoUrl
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('organization.errors.updateFailed'));
      }

      const data = await response.json();
      console.log('✅ Backend response:', data);

      // ✅ Update localStorage nur wenn eigene Org
      if (!viewingOrgId || userRole !== 'super_admin') {
        const user = JSON.parse(localStorage.getItem('user'));
        user.organization = {
          ...user.organization,
          name: data.name,
          color: data.color,
          logo_url: data.logo_url
        };
        localStorage.setItem('user', JSON.stringify(user));
        console.log('💾 LocalStorage updated:', user.organization);
      }

      setMessage({ type: 'success', text: t('organization.success.settingsSaved') });
      setLogoDeleted(false);
      setLogo(null);
      
      // Notify parent component
      if (onUpdate) {
        onUpdate(data);
      }

      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);

    } catch (error) {
      console.error('❌ Save error:', error);
      setMessage({ type: 'error', text: error.message || t('organization.errors.saveFailed') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
            <Building2 className="text-primary-600" size={32} />
            {t('organization.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('organization.subtitle')}
          </p>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800' 
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            <span>{message.text}</span>
          </div>
        )}

        {/* Settings Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          {/* Preview Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('organization.preview.title')}
            </h2>
            <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div 
                className="w-16 h-16 rounded-lg flex items-center justify-center font-bold text-2xl shadow-md"
                style={{ 
                  backgroundColor: color === '#FFFFFF' ? '#2563eb' : color,
                  color: '#ffffff'
                }}
              >
                {logoPreview && !logoDeleted ? (
                  <img 
                    src={logoPreview} 
                    alt={t('organization.preview.logoAlt')}
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  name.charAt(0)?.toUpperCase() || 'O'
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {name || t('organization.preview.placeholder')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('organization.preview.type')}
                </p>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="p-6 space-y-6">
            {/* Organization Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('organization.fields.name.label')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder={t('organization.fields.name.placeholder')}
              />
            </div>

            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('organization.fields.logo.label')}
              </label>
              <div className="flex items-center gap-4">
                <div 
                  className="w-20 h-20 rounded-lg flex items-center justify-center text-2xl font-bold text-white shadow-md"
                  style={{ backgroundColor: color === '#FFFFFF' ? '#2563eb' : color }}
                >
                  {logoPreview && !logoDeleted ? (
                    <img 
                      src={logoPreview} 
                      alt={t('organization.fields.logo.alt')}
                      className="w-full h-full object-cover rounded-lg" 
                    />
                  ) : (
                    name.charAt(0)?.toUpperCase() || 'O'
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <label
                    htmlFor="logo-upload"
                    className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    <Upload size={18} />
                    {t('organization.fields.logo.upload')}
                  </label>
                  {(logoPreview && !logoDeleted) && (
                    <button
                      onClick={handleRemoveLogo}
                      className="ml-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                    >
                      {t('organization.fields.logo.remove')}
                    </button>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {t('organization.fields.logo.hint')}
                  </p>
                </div>
              </div>
            </div>

            {/* Brand Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('organization.fields.color.label')}
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-12 w-20 rounded-lg cursor-pointer border border-gray-300 dark:border-gray-600"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono"
                  placeholder="#2563eb"
                />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {t('organization.fields.color.description')}
              </p>
              
              {/* Color Presets */}
              <div className="flex gap-2 mt-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 mr-2 self-center">
                  {t('organization.fields.color.quickColors')}
                </p>
                {['#2563eb', '#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'].map((presetColor) => (
                  <button
                    key={presetColor}
                    onClick={() => setColor(presetColor)}
                    className="w-8 h-8 rounded border-2 border-white dark:border-gray-700 shadow-sm hover:scale-110 transition-transform"
                    style={{ backgroundColor: presetColor }}
                    title={presetColor}
                  />
                ))}
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleSave}
                disabled={loading}
                className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('organization.buttons.saving')}
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    {t('organization.buttons.save')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                {t('organization.info.title')}
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-300">
                {t('organization.info.description')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSettings;